import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { format } from "date-fns";
import type { ReactNode } from "react";

/** Parse "HH:mm", "H:mm", or "HH:mm:ss" to parts; default 09:00 if invalid. */
export function parseHHmm(s: string): { h: number; m: number } {
  const match = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(s.trim());
  if (!match) return { h: 9, m: 0 };
  let h = parseInt(match[1], 10);
  let m = parseInt(match[2], 10);
  if (Number.isNaN(h) || h < 0 || h > 23) h = 9;
  if (Number.isNaN(m) || m < 0 || m > 59) m = 0;
  return { h, m };
}

export function toHHmm(h: number, m: number): string {
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function dateFromHm(h: number, m: number): Date {
  return new Date(2000, 0, 1, h, m, 0, 0);
}

function formatAmPm(h: number, m: number): string {
  return format(dateFromHm(h, m), "h:mm a");
}

/** Display an `HH:mm` string as `h:mm a` (e.g. for lists). */
export function formatTimeDisplayAmPm(hhmm: string): string {
  if (!hhmm.trim()) return "";
  const { h, m } = parseHHmm(hhmm);
  return formatAmPm(h, m);
}

type Period = "AM" | "PM";

function to12HourParts(h24: number, m: number): { hour12: number; minute: number; period: Period } {
  const period: Period = h24 >= 12 ? "PM" : "AM";
  const hour12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return { hour12, minute: m, period };
}

function from12HourParts(hour12: number, minute: number, period: Period): string {
  let h = hour12 % 12;
  if (period === "PM") h += 12;
  return toHHmm(h, minute);
}

const HOUR_OPTIONS = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, "0"));
const MINUTE_OPTIONS = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0"));

type TimePickerFieldProps = {
  label: ReactNode;
  value: string;
  onChange: (next: string) => void;
  required?: boolean;
  id?: string;
  /** Render bare h-10 selects without the padded wrapper box (matches other h-10 inputs). */
  plain?: boolean;
};

/**
 * Time-of-day picker: hour / minute / AM|PM selects; value stored as `HH:mm` (24h).
 */
export function TimePickerField({ label, value, onChange, required, id, plain = false }: TimePickerFieldProps) {
  const { h, m } = parseHHmm(value.trim() ? value : "09:00");
  const { hour12, minute, period } = to12HourParts(h, m);
  const hourValue = String(hour12).padStart(2, "0");
  const minuteValue = String(minute).padStart(2, "0");

  const setParts = (nextHour12: number, nextMinute: number, nextPeriod: Period) => {
    onChange(from12HourParts(nextHour12, nextMinute, nextPeriod));
  };

  return (
    <div className="space-y-2">
      <Label className="text-xs text-muted-foreground" htmlFor={id ? `${id}-hour` : undefined}>
        {label}
        {required ? <span className="text-destructive"> *</span> : null}
      </Label>
      <div
        className={
          plain
            ? "flex items-center gap-1.5"
            : "flex items-center gap-1.5 rounded-lg border border-border bg-muted/20 p-2 shadow-sm"
        }
        role="group"
        aria-label={typeof label === "string" ? label : "Time"}
      >
        <Select
          value={hourValue}
          onValueChange={(v) => setParts(Number(v), minute, period)}
        >
          <SelectTrigger
            id={id ? `${id}-hour` : undefined}
            className="glass-input h-10 w-[4.5rem] tabular-nums px-2 border-border bg-background"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="max-h-60 z-[100]">
            {HOUR_OPTIONS.map((opt) => (
              <SelectItem key={opt} value={opt} className="tabular-nums">
                {opt}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <span className="text-sm font-semibold text-foreground/70 select-none" aria-hidden>
          :
        </span>

        <Select
          value={minuteValue}
          onValueChange={(v) => setParts(hour12, Number(v), period)}
        >
          <SelectTrigger
            id={id ? `${id}-minute` : undefined}
            className="glass-input h-10 w-[4.5rem] tabular-nums px-2 border-border bg-background"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="max-h-60 z-[100]">
            {MINUTE_OPTIONS.map((opt) => (
              <SelectItem key={opt} value={opt} className="tabular-nums">
                {opt}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={period}
          onValueChange={(v) => setParts(hour12, minute, v as Period)}
        >
          <SelectTrigger
            id={id ? `${id}-period` : undefined}
            className="glass-input h-10 w-[4.5rem] px-2 border-border bg-background"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="z-[100]">
            <SelectItem value="AM">AM</SelectItem>
            <SelectItem value="PM">PM</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
